/**
 * Debug utility to control console output across the application
 * 
 * This utility allows you to:
 * 1. Enable/disable console logs for specific components or the entire app
 * 2. Group debug messages by component/feature for easier management
 */

// Set to true to enable all debug output, false to disable all
const DEBUG_ENABLED = false;

// Feature flags to enable specific component debugging
const DEBUG_FEATURES = {
  clock: false,
  calendar: false,
  calculator: false,
  theme: false,
  session: false
};

/**
 * Logger function that respects debug settings
 * @param {string} component - Component/feature name (should match a key in DEBUG_FEATURES)
 * @param {string} message - The message to log
 * @param {any} data - Optional data to log
 */
export const debug = (component, message, data) => {
  // Only log if debugging is enabled globally or for this specific component
  if (DEBUG_ENABLED || DEBUG_FEATURES[component]) {
    if (data !== undefined) {
      console.log(`[${component}] ${message}:`, data);
    } else {
      console.log(`[${component}] ${message}`);
    }
  }
};

/**
 * Enable debugging for specific components
 * @param {string|string[]} components - Component name(s) to enable debugging for
 */
export const enableDebug = (components) => {
  const componentsList = Array.isArray(components) ? components : [components];
  
  componentsList.forEach(component => {
    if (DEBUG_FEATURES.hasOwnProperty(component)) {
      DEBUG_FEATURES[component] = true;
    }
  });
};

/**
 * Disable debugging for specific components
 * @param {string|string[]} components - Component name(s) to disable debugging for
 */
export const disableDebug = (components) => {
  const componentsList = Array.isArray(components) ? components : [components];
  
  componentsList.forEach(component => {
    if (DEBUG_FEATURES.hasOwnProperty(component)) {
      DEBUG_FEATURES[component] = false;
    }
  });
};

/**
 * Enable debugging for all components
 */
export const enableAllDebug = () => {
  Object.keys(DEBUG_FEATURES).forEach(key => {
    DEBUG_FEATURES[key] = true;
  });
};

/**
 * Disable debugging for all components
 */
export const disableAllDebug = () => {
  Object.keys(DEBUG_FEATURES).forEach(key => {
    DEBUG_FEATURES[key] = false;
  });
};

/**
 * Get the current debug state
 */
export const getDebugState = () => {
  return {
    enabled: DEBUG_ENABLED,
    features: { ...DEBUG_FEATURES }
  };
};

/**
 * Expose debugging API to window for console access
 * This allows developers to enable/disable debugging from the browser console
 */
if (typeof window !== 'undefined') {
  // Create a namespace for our debug utilities
  window.appDebug = {
    enable: enableDebug,
    disable: disableDebug,
    enableAll: enableAllDebug,
    disableAll: disableAllDebug,
    state: getDebugState,
    features: DEBUG_FEATURES
  };
  
  // Add a helper message to console on startup
  console.info(
    'Debug utilities available via console. Use:\n' +
    '- window.appDebug.enable("clock") to enable clock debugging\n' +
    '- window.appDebug.disable("calendar") to disable calendar debugging\n' +
    '- window.appDebug.enableAll() to enable all debugging\n' +
    '- window.appDebug.disableAll() to disable all debugging\n' +
    '- window.appDebug.state() to see current debug state\n' +
    'Available features: ' + Object.keys(DEBUG_FEATURES).join(', ')
  );
} 